<script setup lang="ts">
import { ref, onMounted, onBeforeUnmount, computed, nextTick, watchEffect } from 'vue';
import { useResizeObserver } from '@vueuse/core';
import ConnxButton from '@/components/Atoms/ConnxButton.vue';
import ConnxIcon from '@/components/Atoms/ConnxIcon.vue';

const props = defineProps<{ width: number }>();
const scrollContainer = ref<HTMLElement | null>(null);

const isDragging = ref(false);
const startX = ref(0);
const scrollLeftStart = ref(0);

const isOverflowing = ref(false);
const isAtStart = ref(true);
const isAtEnd = ref(false);

const maxWidth = computed(() => {
  return props.width - 320;
});

const updateScrollPosition = () => {
  const el = scrollContainer.value;
  if (!el) return;
  isAtStart.value = el.scrollLeft <= 0;
  isAtEnd.value = el.scrollLeft + el.clientWidth >= el.scrollWidth - 1;
};

const scrollByAmount = (amount: number) => {
  const el = scrollContainer.value;
  if (!el) return;
  el.scrollBy({ left: amount, behavior: 'smooth' });
  // Allow scroll animation to finish before checking position
  setTimeout(updateScrollPosition, 300);
};

const scrollByTotal = (targetLeft: number) => {
  const el = scrollContainer.value;
  if (!el) return;
  el.scrollTo({ left: targetLeft, behavior: 'smooth' });
  setTimeout(updateScrollPosition, 300);
};

const onMouseDown = (e: MouseEvent) => {
  const el = scrollContainer.value;
  if (!el) return;
  isDragging.value = true;
  startX.value = e.pageX - el.offsetLeft;
  scrollLeftStart.value = el.scrollLeft;
};

const onMouseMove = (e: MouseEvent) => {
  const el = scrollContainer.value;
  if (!isDragging.value || !el) return;
  e.preventDefault();
  const x = e.pageX - el.offsetLeft;
  const walk = (x - startX.value) * 1.5;
  el.scrollLeft = scrollLeftStart.value - walk;
  updateScrollPosition();
};

const onMouseUp = () => {
  isDragging.value = false;
};

const onScroll = () => {
  updateScrollPosition();
};

const addEventListeners = () => {
  const el = scrollContainer.value;
  if (!el) return;
  el.addEventListener('mousedown', onMouseDown);
  el.addEventListener('mousemove', onMouseMove);
  el.addEventListener('scroll', onScroll);
  window.addEventListener('mouseup', onMouseUp);
};

const removeEventListeners = () => {
  const el = scrollContainer.value;
  if (!el) return;
  el.removeEventListener('mousedown', onMouseDown);
  el.removeEventListener('mousemove', onMouseMove);
  el.removeEventListener('scroll', onScroll);
  window.removeEventListener('mouseup', onMouseUp);
};

onMounted(async () => {
  addEventListeners();
});

onBeforeUnmount(() => {
  removeEventListeners();
});

useResizeObserver(scrollContainer, () => {
  const el = scrollContainer.value;
  if (!el) return;
  isOverflowing.value = el.scrollWidth > el.clientWidth;
  updateScrollPosition();
});

defineExpose({ scrollByAmount, scrollByTotal, scrollContainer });
</script>

<template>
  <div class="tw-flex tw-items-center tw-flex-grow tw-justify-between">
    <ConnxButton
      button-style="blank"
      v-show="isOverflowing"
      :disabled="isAtStart"
      @click="() => scrollByAmount(-200)"
      title="Scroll Back"
    >
      <ConnxIcon name="fal fa-chevron-left" class="tw-h-6" />
    </ConnxButton>

    <div
      ref="scrollContainer"
      class="tw-flex tw-overflow-x-auto tw-overflow-y-hidden tw-scroll-smooth tw-no-scrollbar tw-gap-2 select-none"
      :style="{
        maxWidth: `${maxWidth}px`,
      }"
    >
      <slot></slot>
    </div>

    <ConnxButton
      button-style="blank"
      v-show="isOverflowing"
      :disabled="isAtEnd"
      @click="() => scrollByAmount(200)"
      title="Scroll Forward"
    >
      <ConnxIcon name="fal fa-chevron-right" class="tw-h-6" />
    </ConnxButton>
  </div>
</template>
